"use client";

import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cn } from "@workspace/ui/lib/utils";
import * as React from "react";

// Create a context to track the active tab value
const TabsValueContext = React.createContext<string | null>(null);

// Enhanced Tabs component to track active tab for animations
const Tabs = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.Root>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>
>(
	(
		{ className, children, value, defaultValue, onValueChange, ...props },
		ref,
	) => {
		// Track the current value internally
		const [internalValue, setInternalValue] = React.useState<
			string | undefined
		>(value || (defaultValue as string | undefined));

		// Update internal value when controlled value changes
		React.useEffect(() => {
			if (value !== undefined) {
				setInternalValue(value);
			}
		}, [value]);

		// Handle value changes
		const handleValueChange = React.useCallback(
			(newValue: string) => {
				setInternalValue(newValue);
				onValueChange?.(newValue);
			},
			[onValueChange],
		);

		return (
			<TabsValueContext.Provider value={internalValue || null}>
				<TabsPrimitive.Root
					ref={ref}
					className={cn(className)}
					value={value}
					defaultValue={defaultValue}
					onValueChange={handleValueChange}
					{...props}
				>
					{children}
				</TabsPrimitive.Root>
			</TabsValueContext.Provider>
		);
	},
);
Tabs.displayName = TabsPrimitive.Root.displayName;

const TabsList = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.List>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
	<TabsPrimitive.List
		ref={ref}
		className={cn(
			"relative inline-flex h-9 items-center justify-center rounded-xl bg-muted/80 p-1 text-muted-foreground",
			className,
		)}
		{...props}
	/>
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.Trigger>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, children, value, ...props }, ref) => {
	// Access the current tab value from context
	const activeValue = React.useContext(TabsValueContext);
	// Determine if this tab is active
	const isActive = activeValue === value;

	return (
		<TabsPrimitive.Trigger
			ref={ref}
			value={value}
			className={cn(
				"relative flex flex-1 cursor-pointer items-center justify-center whitespace-nowrap rounded-lg px-2 py-1 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-[0.99] disabled:pointer-events-none disabled:opacity-50 sm:flex-initial sm:px-3",
				isActive
					? "bg-background text-foreground font-semibold shadow-xs"
					: "text-muted-foreground hover:bg-muted/50 hover:text-foreground/80",
				className,
			)}
			{...props}
		>
			{children}
		</TabsPrimitive.Trigger>
	);
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, children, ...props }, ref) => {
	return (
		<TabsPrimitive.Content
			ref={ref}
			className={cn(
				"mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
				className,
			)}
			{...props}
		>
			<div className="space-y-4">{children}</div>
		</TabsPrimitive.Content>
	);
});
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
